import { supabase } from '../lib/supabase';

export const analyticsService = {
  // Get user activity analytics
  async getUserAnalytics(userId, startDate = null, endDate = null) {
    try {
      let query = supabase?.from('daily_activities')?.select('*')?.eq('user_id', userId)?.order('activity_date', { ascending: false })

      if (startDate) {
        query = query?.gte('activity_date', startDate)
      }
      if (endDate) {
        query = query?.lte('activity_date', endDate)
      }

      const { data, error } = await query

      if (error) {
        return { analytics: [], error }
      }

      return { analytics: data || [], error: null }
    } catch (error) {
      return { 
        analytics: [], 
        error: { message: 'Failed to load analytics.' }
      }
    }
  },

  // Update daily activity
  async updateDailyActivity(userId, activityData) {
    try {
      const targetDate = activityData?.date || new Date()?.toISOString()?.split('T')?.[0]
      
      const { data, error } = await supabase?.from('daily_activities')?.upsert([{
          user_id: userId,
          activity_date: targetDate,
          goals_completed: activityData?.goalsCompleted || 0,
          core4_completed: activityData?.core4Completed || 0,
          reading_completed: activityData?.readingCompleted || false,
          engagement_score: activityData?.engagementScore || 0
        }], {
          onConflict: 'user_id,activity_date'
        })?.select()?.single()

      return { activity: data, error }
    } catch (error) {
      return { 
        activity: null, 
        error: { message: 'Failed to update activity.' }
      }
    }
  },

  // Calculate user streak
  async getUserStreak(userId) {
    try {
      const { data, error } = await supabase?.from('daily_activities')?.select('activity_date, engagement_score')?.eq('user_id', userId)?.gt('engagement_score', 0)?.order('activity_date', { ascending: false })?.limit(100)

      if (error) {
        return { streak: 0, error }
      }

      // Calculate consecutive days
      let streak = 0
      const today = new Date()
      today?.setHours(0, 0, 0, 0)

      for (const activity of data || []) {
        const activityDate = new Date(activity?.activity_date)
        activityDate?.setHours(0, 0, 0, 0)
        
        const expectedDate = new Date(today)
        expectedDate?.setDate(today?.getDate() - streak)
        
        if (activityDate?.getTime() === expectedDate?.getTime()) {
          streak++
        } else {
          break
        }
      }

      return { streak, error: null }
    } catch (error) {
      return { 
        streak: 0, 
        error: { message: 'Failed to calculate streak.' }
      }
    }
  },

  // Admin analytics - get all users overview
  async getClientStats() {
    try {
      // Get user counts by status
      const { data: userCounts, error: userError } = await supabase?.from('user_profiles')?.select('status')?.eq('role', 'client')

      if (userError) {
        return { stats: null, error: userError }
      }

      // Get completion rates
      const { data: core4Data, error: core4Error } = await supabase?.from('core4_entries')?.select('user_id, status, area')

      if (core4Error) {
        return { stats: null, error: core4Error }
      }

      // Get reading progress
      const { data: readingData, error: readingError } = await supabase?.from('reading_progress')?.select('user_id, is_completed')

      if (readingError) {
        return { stats: null, error: readingError }
      }

      // Calculate statistics
      const totalClients = userCounts?.length || 0
      const activeClients = userCounts?.filter(u => u?.status === 'active')?.length || 0
      const completedClients = userCounts?.filter(u => u?.status === 'completed')?.length || 0

      // CORE4 statistics
      const core4Stats = {
        faith: { completed: 0, total: 0 },
        family: { completed: 0, total: 0 },
        finances: { completed: 0, total: 0 },
        fitness: { completed: 0, total: 0 }
      }

      core4Data?.forEach(entry => {
        const area = entry?.area
        if (core4Stats?.[area]) {
          core4Stats[area].total++
          if (entry?.status === 'completed') {
            core4Stats[area].completed++
          }
        }
      })

      // Reading completion rate
      const totalReadings = readingData?.length || 0
      const completedReadings = readingData?.filter(r => r?.is_completed)?.length || 0
      const readingCompletionRate = totalReadings > 0 ? (completedReadings / totalReadings) * 100 : 0

      const stats = {
        totalClients,
        activeClients,
        completedClients,
        averageCompletion: readingCompletionRate,
        core4Stats,
        curriculumCompletions: completedClients
      }

      return { stats, error: null }
    } catch (error) {
      return { 
        stats: null, 
        error: { message: 'Failed to load client statistics.' }
      }
    }
  },

  // Get user list with basic stats (for admin)
  async getUserList() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          *,
          daily_activities!daily_activities_user_id_fkey (
            activity_date,
            engagement_score
          )
        `)?.eq('role', 'client')?.order('created_at', { ascending: false })

      if (error) {
        return { users: [], error }
      }

      // Calculate last activity for each user
      const usersWithActivity = data?.map(user => {
        const activities = user?.daily_activities || []
        const lastActivity = activities?.reduce((latest, activity) => {
          const activityDate = new Date(activity?.activity_date)
          return !latest || activityDate > new Date(latest) ? activity?.activity_date : latest
        }, null)

        return {
          ...user,
          lastActivity,
          daily_activities: undefined // Remove nested data
        }
      })

      return { users: usersWithActivity || [], error: null }
    } catch (error) {
      return { 
        users: [], 
        error: { message: 'Failed to load user list.' }
      }
    }
  }
}